import { Box } from 'atomic-layout';
import { FORM_ERROR } from 'final-form';
import PageTitle from 'components/layout/pageTitle';
import SEO from 'components/seo';
import axios from 'axios';
import dynamic from 'next/dynamic';
import { getFooterProps } from 'components/layout/footer';
import { getHeaderProps } from 'components/layout/header';
import { getLoaderProps } from 'components/loader';
import strapi from 'utils/strapi';
import styled from 'styled-components';
import { useState } from 'react';

const CalculatorCard = dynamic(
  async () => (await import('components/atoms/Cards')).CalculatorCard
);
const CgSpinnerTwoAlt = dynamic(
  async () => (await import('react-icons/cg')).CgSpinnerTwoAlt
);
const MarkdownText = dynamic(() => import('components/atoms/markdown'));

const Input = dynamic(
  async () => (await import('components/atoms/form')).Input
);
const StyledField = dynamic(
  async () => (await import('components/atoms/form')).StyledField
);
const Textarea = dynamic(
  async () => (await import('components/atoms/form')).Textarea
);

const Form = dynamic(async () => (await import('react-final-form')).Form);
const Field = dynamic(async () => (await import('react-final-form')).Field);

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.colors.secondaryGradient};
  border-radius: 5px;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textInverse};
`;

const required = (value) => (!!value ? undefined : 'This field is required');

const Spinner = styled(CgSpinnerTwoAlt)`
  width: 30px;
  height: 30px;
  animation-name: spin;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export default function ContactPage({ seoData, text, privacyText }) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  return (
    <>
      <SEO {...seoData} />
      <PageTitle
        $marginBottom={'5rem'}
        title={'Contact <gradient>SatsGoal<gradient>'}
        description={text}
      />
      <Box
        maxWidth="600px"
        marginHorizontal="auto"
        width="100%"
        style={{ boxSizing: 'border-box' }}
        paddingHorizontal={1}
      >
        <CalculatorCard>
          <Form
            onSubmit={async (values) => {
              setLoading(true);
              try {
                const { data } = await axios.post(
                  'https://api.formjam.io/form/da4f18ae-2025-4264-b4d4-e17f4edf7c5b',
                  values
                );
              } catch (err) {
                console.error('Error', err);
                setLoading(false);
                return (
                  err.response?.data || { [FORM_ERROR]: 'Error submitting.' }
                );
              }
              setLoading(false);
              setSubmitted(true);
            }}
          >
            {({ handleSubmit }) =>
              !submitted && !loading ? (
                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  {/* Honeypot */}
                  <Field type="hidden" name="_f2NRWLP3o69c">
                    {({ input }) => <input {...input} value="" />}
                  </Field>
                  <Box flex flexDirection="column" gapRow={2} width="100%">
                    <Field name="email" type="email" validate={required}>
                      {({ input, meta }) => (
                        <StyledField
                          label="Email Address"
                          error={meta.touched && meta.error}
                          style={{ width: '100%' }}
                        >
                          <Input {...input} style={{ border: 'none' }} />
                        </StyledField>
                      )}
                    </Field>
                    <Box flex gap={2}>
                      <Field name="firstName" validate={required}>
                        {({ input, meta }) => (
                          <StyledField
                            label="First Name"
                            error={meta.touched && meta.error}
                            style={{ flex: 1 }}
                          >
                            <Input {...input} style={{ border: 'none' }} />
                          </StyledField>
                        )}
                      </Field>
                      <Field name="lastName" validate={required}>
                        {({ input, meta }) => (
                          <StyledField
                            label="Last Name"
                            error={meta.touched && meta.error}
                            style={{ flex: 1 }}
                          >
                            <Input {...input} style={{ border: 'none' }} />
                          </StyledField>
                        )}
                      </Field>
                    </Box>
                    <Field name="message" validate={required}>
                      {({ input, meta }) => (
                        <StyledField
                          label="Your Message"
                          error={meta.touched && meta.error}
                          style={{ width: '100%' }}
                        >
                          <Textarea {...input} style={{ border: 'none' }} />
                        </StyledField>
                      )}
                    </Field>
                    <Field
                      name="consentGiven"
                      type="checkbox"
                      validate={required}
                    >
                      {({ input, meta }) => (
                        <StyledField
                          style={{
                            width: '100%',
                            marginTop: '-20px',
                            marginBottom: '-20px',
                          }}
                          error={meta.touched && meta.error}
                        >
                          <Box gap={1} flex alignItems="center">
                            <input id="consentGiven" {...input} />
                            <label for="consentGiven">
                              <MarkdownText>{privacyText}</MarkdownText>
                            </label>
                          </Box>
                        </StyledField>
                      )}
                    </Field>
                    <Box flex justifyContent="flex-end" padding={1}>
                      <SubmitButton type="submit">Submit Message</SubmitButton>
                    </Box>
                  </Box>
                </form>
              ) : !!loading ? (
                <div>
                  <Spinner />
                </div>
              ) : (
                <p>Thanks, your enquiry has been submitted!</p>
              )
            }
          </Form>
        </CalculatorCard>
      </Box>
    </>
  );
}

export async function getStaticProps(ctx) {
  let result;
  try {
    result = await strapi('/api/contact-page', {
      populate: ['seo', 'seo.openGraphImage'],
    });
  } catch (err) {
    console.error('Error', err);
    return {
      notFound: true,
    };
  }
  const { data } = result;

  return {
    props: {
      seoData: data.attributes.seo,
      text: data.attributes.text || '',
      privacyText: data.attributes.privacyText || '',
      footerProps: await getFooterProps(),
      headerProps: await getHeaderProps(),
      loaderProps: await getLoaderProps(),
    },
  };
}
